import { useState } from "react";

import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import type { NoteType, UUID, Addendum } from "@/core/types";

import { AddendumActions } from "./AddendumActions";
import { AddendumCosignatureReview } from "./(cosignature)/AddendumCosignatureReview";
import { AddendumDisplay } from "./AddendumDisplayRouter";
import { AddendumRejectionBanner } from "./(rejection)/AddendumRejectionBanner";
import { EditGeneralAddendum } from "./(general)/EditGeneralAddendum";

type NoteAddendumProps = {
  addendum: Addendum;
  credentials: string | undefined;
  isAuthor: boolean;
  noteId: UUID;
  noteType: NoteType | null;
  patientId: UUID;
  signature: string | undefined;
  textTestId?: string;
};

export function NoteAddendum({
  addendum,
  credentials,
  isAuthor,
  noteId,
  noteType,
  patientId,
  signature,
  textTestId,
}: NoteAddendumProps) {
  const [isEditMode, setIsEditMode] = useState(false);

  const isRejected = addendum.addendum_state === "rejected";

  return (
    <DashboardCard2
      Actions={
        <AddendumActions
          addendum={addendum}
          isAuthor={isAuthor}
          isEditMode={isEditMode}
          isRejected={isRejected}
          onEditPress={() => setIsEditMode(true)}
        />
      }
      classNameChildren="tw:flex tw:flex-col tw:gap-6"
      id={`addendum-${addendum.addendum_id}`}
      title="Addendum"
    >
      {isEditMode ? (
        // ACA addendums can never be rejected (ACA is excluded from the note-level
        // supervision whitelist, so their addendums always auto-complete without
        // ever entering the cosign pipeline) — editing is only reachable for
        // addendums that support cosign rejection, i.e. never ACA. No note-type
        // branching needed here.
        <EditGeneralAddendum
          addendumId={addendum.addendum_id}
          credentials={credentials}
          defaultValue={addendum.addendum}
          noteId={noteId}
          onCancel={() => setIsEditMode(false)}
          onSuccess={() => setIsEditMode(false)}
          patientId={patientId}
          signature={signature}
        >
          <AddendumRejectionBanner addendumId={addendum.addendum_id} />
        </EditGeneralAddendum>
      ) : (
        <>
          <AddendumDisplay addendum={addendum} noteType={noteType} textTestId={textTestId} />
          {isRejected ? <AddendumRejectionBanner addendumId={addendum.addendum_id} /> : null}
        </>
      )}

      {!isEditMode && <AddendumCosignatureReview addendum={addendum} patientId={patientId} />}
    </DashboardCard2>
  );
}
